import { FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * Sign-up flow. The mockup shows three steps (Details → ID upload → Done) but
 * ID upload is gated behind a backend feature we haven't shipped yet, so we
 * collect ID at signup time but skip the upload step until the file pipeline
 * is ready. The visual stays close to the spec — step indicator, single
 * details form, success screen.
 */
export function SignUp() {
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState<'details' | 'done'>('details');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [showPwd2, setShowPwd2] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function validate(): string | null {
    if (!firstName.trim()) return 'Please enter your first name.';
    if (!lastName.trim()) return 'Please enter your last name.';
    if (!email.trim()) return 'Please enter your email.';
    if (pwd.length < 8) return 'Password must be at least 8 characters.';
    if (pwd !== pwd2) return "Passwords don't match.";
    return null;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      // Pass first/last/phone through to Supabase Auth user_metadata —
      // the profiles INSERT trigger picks them up and populates the row,
      // so admin sees the customer's real name from the first sign-up.
      await signUp(email, pwd, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone: phone.trim() || undefined
      });
      setStep('done');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create account');
    } finally {
      setSubmitting(false);
    }
  }

  if (step === 'done') {
    return (
      <section className="screen">
        <div className="scroll auth-scroll">
          <div className="step-indicator">
            <SiStep done label="Details" />
            <SiStep done label="ID Upload" />
            <SiStep active label="Complete" />
          </div>
          <div className="success" style={{ paddingTop: 30 }}>
            <div className="check">
              <MailBig />
            </div>
            <h2>PLEASE CONFIRM YOUR ACCOUNT</h2>
            <div className="p">
              We&apos;ve sent a confirmation link to{' '}
              <strong>{email || 'your inbox'}</strong>. Click the link in that
              email to activate your account, then sign in.
            </div>
            <div
              className="p"
              style={{ marginTop: 12, fontSize: 12, color: 'var(--ink-soft)' }}
            >
              Can&apos;t see it? Check your spam folder — it usually arrives
              within a minute.
            </div>
          </div>
          <div style={{ padding: '20px 16px 0' }}>
            <div className="info-card">
              <div className="ic">
                <MailIcon />
              </div>
              <div>
                <div className="h">Check your email</div>
                <div className="p">
                  Open the message from Label Rotation and tap{' '}
                  <em>Confirm your email</em>.
                </div>
              </div>
            </div>
            <div className="info-card">
              <div className="ic">
                <Lock />
              </div>
              <div>
                <div className="h">Then sign in</div>
                <div className="p">
                  Once confirmed, log in to start browsing and verify your ID.
                </div>
              </div>
            </div>
          </div>
          <div style={{ padding: '24px 16px 48px' }}>
            <button
              className="cta-full"
              onClick={() => navigate('/sign-in', { replace: true })}
            >
              <span>GO TO SIGN IN</span>
              <Arrow />
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="screen">
      <form onSubmit={onSubmit} style={{ display: 'contents' }}>
        <div className="scroll auth-scroll">
          <div className="step-indicator">
            <SiStep active label="Details" />
            <SiStep label="ID Upload" />
            <SiStep label="Complete" />
          </div>

          <div className="acc-screen">
            <h1 className="acc-h1">CREATE YOUR ACCOUNT</h1>
            <div className="acc-sub">Let&apos;s get you set up.</div>

            <div className="form-field">
              <label>First name</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="John"
                autoComplete="given-name"
                required
              />
            </div>
            <div className="form-field">
              <label>Last name</label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Smith"
                autoComplete="family-name"
                required
              />
            </div>
            <div className="form-field">
              <label>Email address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="john@email.com"
                autoComplete="email"
                required
              />
            </div>
            <div className="form-field">
              <label>Phone number</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="07123 456789"
                autoComplete="tel"
                inputMode="tel"
              />
            </div>
            <div className="form-field">
              <label>Password</label>
              <input
                type={showPwd ? 'text' : 'password'}
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                placeholder="••••••••"
                autoComplete="new-password"
                minLength={8}
                required
              />
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                aria-label={showPwd ? 'Hide password' : 'Show password'}
                style={{
                  position: 'absolute',
                  right: 12,
                  top: 38,
                  width: 28,
                  height: 28,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--ink-soft)'
                }}
              >
                <EyeIcon />
              </button>
            </div>
            <div className="form-field">
              <label>Confirm password</label>
              <input
                type={showPwd2 ? 'text' : 'password'}
                value={pwd2}
                onChange={(e) => setPwd2(e.target.value)}
                placeholder="••••••••"
                autoComplete="new-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPwd2((v) => !v)}
                aria-label={showPwd2 ? 'Hide password' : 'Show password'}
                style={{
                  position: 'absolute',
                  right: 12,
                  top: 38,
                  width: 28,
                  height: 28,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--ink-soft)'
                }}
              >
                <EyeIcon />
              </button>
            </div>

            {error && <div className="form-error">{error}</div>}
          </div>
        </div>

        <div className="pd-cta">
          <button type="submit" className="cta-full" disabled={submitting}>
            <span>{submitting ? 'CREATING…' : 'CREATE ACCOUNT'}</span>
            <Arrow />
          </button>
          <div className="form-link" style={{ marginTop: 10 }}>
            Already have an account? <Link to="/sign-in">Log in</Link>
          </div>
        </div>
      </form>
    </section>
  );
}

function SiStep({ active, done, label }: { active?: boolean; done?: boolean; label: string }) {
  const cls = ['si-step', active ? 'active' : '', done ? 'done' : ''].filter(Boolean).join(' ');
  const idx = label === 'Details' ? 1 : label === 'ID Upload' ? 2 : 3;
  return (
    <div className={cls}>
      <div className="dot">
        {done ? (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        ) : (
          idx
        )}
      </div>
      <div className="lbl">{label}</div>
    </div>
  );
}
function Arrow() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  );
}
function CheckBig() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
function MailBig() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 7l9 6 9-6" />
    </svg>
  );
}
function MailIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 7l9 6 9-6" />
    </svg>
  );
}
function EyeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}
function Lock() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 018 0v4" />
    </svg>
  );
}
function BagIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M6 7h12l-1 13H7L6 7z" />
      <path d="M9 7a3 3 0 016 0" />
    </svg>
  );
}
